// Écran d'import (NutriScan 10, périmètre § 1-8) : sélection multiple depuis la pellicule (l'input
// qui déclenche `ouvrirAvec` est SANS l'attribut `capture`, à la différence de `#photo-input` de
// main.ts — les deux coexistent, cf. main.ts pour pourquoi), file de traitement EN SÉRIE avec
// progression (web/src/import/importRunner.ts), date par photo résolue depuis l'EXIF et modifiable
// avant enregistrement, coût annoncé avant de démarrer (web/src/import/estimateClient.ts), chaque
// photo réussie enregistrée directement dans le journal avec `source: 'import'`.
//
// Poids retenu par défaut = milieu de la fourchette du modèle (result/portions.ts `poidsInitial`),
// SANS curseur de correction ici : un import de 30 photos vise la vitesse, pas 30 ajustements un par
// un. Limitation assumée (résumé de PR) — resultatView.ts reste le chemin à curseur pour une photo
// prise sur le vif, où une seule portion est en jeu.
import { compresserImage, dependancesNavigateur } from '../capture/compression.js';
import { dateDepuisFichier, type OrigineDate } from '../capture/exifDate.js';
import { analyserPhoto } from '../analyze/analyzeClient.js';
import { televerserPhoto } from '../analyze/uploadClient.js';
import { estimerImport, EstimationError } from '../import/estimateClient.js';
import { traiterImportsEnSerie, type DependancesImport } from '../import/importRunner.js';
import type { PhotoImportee, ResultatImportPhoto } from '../import/types.js';
import { enregistrerRepas, JournalError } from '../journal/journalClient.js';
import type { AlimentRepas } from '../journal/types.js';
import { alimentDepuisDetection, macrosCourantes } from '../result/portions.js';

export interface VueImport {
  readonly element: HTMLElement;
  /** Ouvre l'écran avec les fichiers choisis (depuis un `<input type="file" multiple>` sans `capture`). */
  ouvrirAvec(fichiers: FileList | File[]): void;
}

let compteurId = 0;
function idLocal(): string {
  compteurId += 1;
  return `import-${compteurId}`;
}

/** `Date` locale -> valeur d'un `<input type="datetime-local">` (`YYYY-MM-DDTHH:mm`, pas de fuseau). */
function dateEnValeurInput(date: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Inverse de `dateEnValeurInput` — reconstruit depuis les champs plutôt que `new Date(valeur)` sur
 *  la chaîne brute : un `<input type="datetime-local">` n'a pas de fuseau, `Date` en suppose un. */
function dateDepuisValeurInput(valeur: string): Date | null {
  const [datePart, heurePart] = valeur.split('T');
  if (!datePart || !heurePart) return null;
  const [annee, mois, jour] = datePart.split('-').map(Number);
  const [heure, minute] = heurePart.split(':').map(Number);
  if (!annee || !mois || !jour || heure === undefined || minute === undefined) return null;
  const date = new Date(annee, mois - 1, jour, heure, minute);
  return Number.isNaN(date.getTime()) ? null : date;
}

function libelleOrigine(origine: OrigineDate): string {
  if (origine === 'exif') return 'date de la photo (EXIF)';
  if (origine === 'lastModified') return 'date du fichier';
  return 'aucune date trouvée, à corriger';
}

export function creerVueImport(options: { onFermer: () => void }): VueImport {
  const element = document.createElement('section');
  element.className = 'import';
  element.hidden = true;

  const entete = document.createElement('div');
  entete.className = 'import-entete';
  const titre = document.createElement('h2');
  titre.textContent = 'Importer des photos';
  const fermerBtn = document.createElement('button');
  fermerBtn.type = 'button';
  fermerBtn.className = 'import-fermer';
  fermerBtn.textContent = '← Retour';
  entete.append(titre, fermerBtn);

  const coutEl = document.createElement('p');
  coutEl.className = 'import-cout';
  coutEl.setAttribute('role', 'status');
  coutEl.setAttribute('aria-live', 'polite');

  const listeEl = document.createElement('ul');
  listeEl.className = 'import-liste';

  const progresEl = document.createElement('p');
  progresEl.className = 'import-progres';
  progresEl.setAttribute('role', 'status');
  progresEl.setAttribute('aria-live', 'polite');

  const demarrerBtn = document.createElement('button');
  demarrerBtn.type = 'button';
  demarrerBtn.className = 'primaire';
  demarrerBtn.textContent = "Démarrer l'import";
  demarrerBtn.disabled = true;

  const annulerBtn = document.createElement('button');
  annulerBtn.type = 'button';
  annulerBtn.className = 'import-annuler';
  annulerBtn.textContent = 'Annuler';
  annulerBtn.hidden = true;

  element.append(entete, coutEl, listeEl, progresEl, demarrerBtn, annulerBtn);

  let photos: PhotoImportee[] = [];
  const lignesParId = new Map<string, { dateInput: HTMLInputElement; origineEl: HTMLElement; statutEl: HTMLElement }>();
  // Photos dont l'utilisateur a corrigé la date à la main : la résolution EXIF, lancée en parallèle
  // pour tout le lot et pas forcément terminée avant que l'utilisateur touche une ligne (15 photos de
  // plusieurs Mo depuis une pellicule, ce n'est pas instantané), ne doit JAMAIS écraser cette saisie
  // quand elle se résout après coup (§ « l'EXIF ment parfois »).
  const datesModifieesParUtilisateur = new Set<string>();
  let controleur: AbortController | null = null;

  function reinitialiser(): void {
    listeEl.replaceChildren();
    lignesParId.clear();
    datesModifieesParUtilisateur.clear();
    progresEl.textContent = '';
    coutEl.textContent = '';
    demarrerBtn.hidden = false;
    demarrerBtn.disabled = true;
    annulerBtn.hidden = true;
  }

  fermerBtn.addEventListener('click', () => {
    controleur?.abort();
    element.hidden = true;
    options.onFermer();
  });

  function creerLigne(photo: PhotoImportee): HTMLLIElement {
    const li = document.createElement('li');
    li.className = 'import-ligne';
    li.dataset.id = photo.id;

    const nom = document.createElement('p');
    nom.className = 'import-ligne-nom';
    nom.textContent = photo.nom;

    const dateLabel = document.createElement('label');
    dateLabel.className = 'import-ligne-date';
    dateLabel.append('Date du repas : ');
    const dateInput = document.createElement('input');
    dateInput.type = 'datetime-local';
    dateInput.value = dateEnValeurInput(photo.date);
    // Un repas consommé dans le futur est refusé côté serveur (consomme_le_futur) : autant l'éviter
    // dans le sélecteur plutôt que de laisser l'utilisateur découvrir le 400 après le lancement.
    dateInput.max = dateEnValeurInput(new Date());
    dateInput.addEventListener('change', () => {
      const date = dateDepuisValeurInput(dateInput.value);
      const cible = photos.find((p) => p.id === photo.id);
      if (date && cible) cible.date = date;
      datesModifieesParUtilisateur.add(photo.id);
    });
    dateLabel.append(dateInput);
    const origineEl = document.createElement('span');
    origineEl.className = 'import-ligne-origine';
    origineEl.textContent = libelleOrigine(photo.origineDate);
    dateLabel.append(' ', origineEl);

    const statutEl = document.createElement('p');
    statutEl.className = 'import-ligne-statut';
    statutEl.textContent = 'En attente';

    li.append(nom, dateLabel, statutEl);
    lignesParId.set(photo.id, { dateInput, origineEl, statutEl });
    return li;
  }

  async function actualiserEstimation(): Promise<void> {
    if (photos.length === 0) return;
    coutEl.textContent = 'Estimation du coût…';
    try {
      const estimation = await estimerImport(photos.length);
      if (estimation.coutTotalEstimeEur === null) {
        // Modèle sans ligne de tarif connue (estimate.ts) : coût inconnu, pas une erreur — on ne
        // bloque pas l'import sur une inconnue.
        coutEl.textContent = `Coût de l'import non estimable pour ${estimation.nbPhotos} photo(s) (modèle sans tarif connu).`;
      } else if (estimation.budgetSuffisant) {
        coutEl.textContent = `Coût estimé pour ${estimation.nbPhotos} photo(s) : ~${estimation.coutTotalEstimeEur.toFixed(4)} €.`;
      } else {
        coutEl.textContent =
          `Coût estimé pour ${estimation.nbPhotos} photo(s) : ~${estimation.coutTotalEstimeEur.toFixed(4)} € — ` +
          `le budget du jour de NutriScan (${estimation.plafondEur.toFixed(2)} €, déjà ${estimation.cumulEur.toFixed(4)} € dépensés) ` +
          'ne suffira pas pour tout le lot : réduis le nombre de photos, ou réessaie demain.';
      }
      demarrerBtn.disabled = !estimation.budgetSuffisant;
    } catch (err) {
      // L'estimation est un CONFORT (§ « coût annoncé ») : son indisponibilité n'empêche pas
      // d'importer, elle prévient juste moins bien — le garde-fou serveur reste la ligne de défense
      // réelle (api/src/budgetGuard.ts, appliqué à chaque analyse individuellement).
      coutEl.textContent = err instanceof EstimationError ? err.message : "Impossible d'estimer le coût de l'import.";
      demarrerBtn.disabled = false;
    }
  }

  function deps(): DependancesImport {
    return {
      lireEntete: async (fichier) => new Uint8Array(await fichier.slice(0, 64).arrayBuffer()),
      compresser: (fichier) => compresserImage(fichier, dependancesNavigateur()),
      televerser: (blob, opts) => televerserPhoto(blob, opts),
      analyser: (blobId, ecouteurs, opts) =>
        analyserPhoto(blobId, { surAliment: ecouteurs.surAliment, surReprise: ecouteurs.surReprise }, opts),
    };
  }

  /** Enregistre une analyse réussie dans le journal, au poids par défaut (milieu de fourchette,
   *  sans curseur ici) — rend un message de statut à afficher, jamais ne lève. */
  async function enregistrer(resultat: ResultatImportPhoto, photo: PhotoImportee): Promise<string> {
    if (!resultat.aliments || resultat.aliments.length === 0) return 'Aucun aliment détecté.';
    const aliments: AlimentRepas[] = resultat.aliments
      .map((detecte, i) => alimentDepuisDetection(detecte, `${photo.id}-${i}`))
      .map((ajustable) => ({ nom: ajustable.nom, poidsG: ajustable.poidsG, macros: macrosCourantes(ajustable) }))
      .filter((a): a is AlimentRepas => a.macros !== null);
    if (aliments.length === 0) return 'Composition inconnue : non enregistré.';
    try {
      await enregistrerRepas({ aliments, source: 'import', analyseId: resultat.analyse?.analyseId, consommeLe: photo.date.toISOString() });
      return 'Enregistré dans le journal ✓';
    } catch (err) {
      return err instanceof JournalError ? err.message : "L'enregistrement a échoué.";
    }
  }

  async function demarrer(): Promise<void> {
    demarrerBtn.hidden = true;
    annulerBtn.hidden = false;
    controleur = new AbortController();

    const resultats = await traiterImportsEnSerie(
      photos,
      deps(),
      (resultat, index, total) => {
        progresEl.textContent = `${index + 1} / ${total} — ${resultat.nom}`;
        const ligne = lignesParId.get(resultat.id);
        if (!ligne) return;
        if (resultat.statut === 'en_cours') ligne.statutEl.textContent = 'Traitement…';
        else if (resultat.statut === 'echec') ligne.statutEl.textContent = resultat.erreur ?? 'Échec.';
        else if (resultat.statut === 'ok') {
          ligne.statutEl.textContent = 'Analysé — enregistrement…';
          const photo = photos.find((p) => p.id === resultat.id);
          if (photo) {
            void enregistrer(resultat, photo).then((message) => {
              ligne.statutEl.textContent = message;
            });
          }
        }
      },
      controleur.signal,
    );

    annulerBtn.hidden = true;
    const ok = resultats.filter((r) => r.statut === 'ok').length;
    progresEl.textContent = `Terminé : ${ok} / ${photos.length} photo(s) analysée(s) avec succès.`;
  }

  demarrerBtn.addEventListener('click', () => void demarrer());
  annulerBtn.addEventListener('click', () => controleur?.abort());

  return {
    element,
    ouvrirAvec(fichiers) {
      element.hidden = false;
      reinitialiser();
      const tableau = Array.from(fichiers);
      photos = tableau.map((fichier) => ({
        id: idLocal(),
        fichier,
        nom: fichier.name,
        date: new Date(),
        origineDate: 'maintenant' as const,
      }));
      for (const photo of photos) listeEl.append(creerLigne(photo));
      // La résolution EXIF (lecture de fichier) se fait APRÈS l'affichage : la liste apparaît tout
      // de suite, chaque date se met à jour dès qu'elle est connue (pas de blocage sur la première).
      for (const photo of photos) {
        void dateDepuisFichier(photo.fichier).then((resolue) => {
          // La lecture EXIF peut se terminer APRÈS que l'utilisateur a corrigé cette ligne à la
          // main : ne jamais écraser une date saisie, même une fois l'EXIF enfin connu.
          if (datesModifieesParUtilisateur.has(photo.id)) return;
          photo.date = resolue.date;
          photo.origineDate = resolue.origine;
          const ligne = lignesParId.get(photo.id);
          if (ligne) {
            ligne.dateInput.value = dateEnValeurInput(resolue.date);
            ligne.origineEl.textContent = libelleOrigine(resolue.origine);
          }
        });
      }
      void actualiserEstimation();
    },
  };
}
